import React, { useState, useRef } from 'react';
import safeStorage from '../../utils/safeStorage';

const API_URL = process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

/**
 * Thumbs-up / thumbs-down button with a hover tooltip showing who reacted.
 *
 * Props:
 *   type        – 'up' | 'down'
 *   count       – number of reactions of this type
 *   active      – boolean, whether the current user has this reaction
 *   onClick     – handler called when the button is clicked
 *   disabled    – boolean (true when user is not logged in)
 *   entityType  – 'topic' | 'message'
 *   entityId    – numeric entity id
 *   minWidth    – optional CSS string e.g. '48px'
 */
function ReactionButton({ type, count, active, onClick, disabled, entityType, entityId, minWidth }) {
  const [reactors, setReactors] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const timerRef = useRef(null);

  const handleMouseEnter = () => {
    setShowTooltip(true);
    if (count > 0) {
      timerRef.current = setTimeout(async () => {
        try {
          const token = safeStorage.getItem('authToken');
          const headers = token ? { Authorization: `Bearer ${token}` } : {};
          const res = await fetch(
            `${API_URL}/discussion-reactions/reactors?entity_type=${encodeURIComponent(entityType)}&entity_id=${encodeURIComponent(entityId)}&reaction_type=${encodeURIComponent(type)}`,
            { headers }
          );
          const data = await res.json();
          setReactors(data.reactors || []);
        } catch (_) {
          setReactors([]);
        }
      }, 300);
    }
  };

  const handleMouseLeave = () => {
    clearTimeout(timerRef.current);
    setShowTooltip(false);
    setReactors(null);
  };

  const buttonStyle = {
    background: active ? (type === 'up' ? '#c6f6d5' : '#fed7d7') : '#f0f4f8',
    color: active ? (type === 'up' ? '#276749' : '#c53030') : '#718096',
    border: `1px solid ${active ? (type === 'up' ? '#9ae6b4' : '#feb2b2') : '#e2e8f0'}`,
    borderRadius: '12px',
    padding: '3px 10px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: !disabled ? 'pointer' : 'default',
    transition: 'background 0.15s, color 0.15s',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    ...(minWidth ? { minWidth } : {}),
  };

  const emoji = type === 'up' ? '👍' : '👎';

  let tooltipContent = null;
  if (showTooltip && count > 0) {
    let label;
    if (!reactors) {
      label = 'Loading…';
    } else if (reactors.length === 0) {
      label = 'No reactions';
    } else {
      const shown = reactors.slice(0, 5);
      const rest = reactors.length - 5;
      label = shown.join(', ') + (rest > 0 ? ` +${rest} more` : '');
    }
    tooltipContent = (
      <div style={{
        position: 'absolute',
        bottom: 'calc(100% + 6px)',
        left: '50%',
        transform: 'translateX(-50%)',
        background: '#1a202c',
        color: 'white',
        borderRadius: '6px',
        padding: '5px 10px',
        fontSize: '12px',
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
        zIndex: 1000,
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
      }}>
        {label}
        {/* down-pointing caret */}
        <div style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '5px solid transparent',
          borderRight: '5px solid transparent',
          borderTop: '5px solid #1a202c',
        }} />
      </div>
    );
  }

  return (
    <div
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        onClick={onClick}
        disabled={disabled}
        title={disabled ? 'Log in to react' : (active ? 'Remove reaction' : (type === 'up' ? 'Thumbs up' : 'Thumbs down'))}
        style={buttonStyle}
      >
        {emoji} {count || 0}
      </button>
      {tooltipContent}
    </div>
  );
}

export default ReactionButton;
